import { useState } from "react";
import { useT } from "../i18n";
import { Knopf, euro } from "./index";
import { usePositionAendern } from "../daten/haken";
import { useRuf } from "../schale/Ruf";
import { zahlAnzeige } from "../daten/zahlfeld";
import { vorschlagRumpf, vorschlagSumme, type Zeitraumvorschlag } from "../daten/zeitraumvorschlag";

/* ══════════════════════════════════════════════════════════════════
   „Neuer Zeitraum — neuer Betrag?" (F3, 04.10.2026; Befund AG-21)

   Luca: *neuen Betrag vorschlagen, der Mensch bestätigt; ein von Hand
   gesetzter Preis bleibt.* Das Band steht unter der Position, deren Zeitraum
   eben geändert wurde, und sagt beide Beträge — vorher und vorgeschlagen.
   „Übernehmen" schreibt Menge (und Satz, wenn er aus dem Stamm kam);
   „So lassen" schließt das Band, und die Zeile bleibt, wie sie ist.
   ══════════════════════════════════════════════════════════════════ */

export function Zeitraumvorschlagband({ nr, id, was, vorschlag, fertig }: {
  nr: string;
  id: string;
  was: string;
  vorschlag: Zeitraumvorschlag;
  fertig: () => void;
}) {
  const t = useT();
  const ruf = useRuf();
  const aendern = usePositionAendern();
  const [laeuft, setzeLaeuft] = useState(false);

  async function uebernehmen() {
    setzeLaeuft(true);
    try {
      await aendern.mutateAsync({ nr, id, ...vorschlagRumpf(vorschlag) });
      ruf("posVorschlagUebernommen");
      fertig();
    } finally {
      setzeLaeuft(false);
    }
  }

  const werteVon = (x: Omit<Zeitraumvorschlag, "anbauteile">, name: string) => ({
    was: name,
    menge: zahlAnzeige(x.menge, 2),
    einzel: euro(x.einzelpreis),
    gesamt: euro(x.gesamt),
    mengeVorher: zahlAnzeige(x.menge_vorher, 2),
    einzelVorher: euro(x.einzelpreis_vorher),
    gesamtVorher: euro(x.gesamt_vorher),
    tage: zahlAnzeige(x.miettage ?? 0, 0),
    tageVorher: zahlAnzeige(x.miettage_vorher ?? 0, 0),
  });
  const werte = werteVon(vorschlag, was);
  const summe = vorschlagSumme(vorschlag);

  return (
    <div className="meldung" role="status" data-vorschlag={id}>
      <span className="pkt acht" style={{ marginTop: 5 }} />
      <div className="meldung-text">
        <b>{t("positionen.vorschlag.titel")}</b>
        <span>{t("positionen.vorschlag.satz", werte)}</span>
        {vorschlag.preis_von_hand ? <span>{t("positionen.vorschlag.vonHand", werte)}</span> : null}
        {/* Angenommen (P4-01): Satz eingefroren, Zuschläge folgen derselben Dauer. */}
        {vorschlag.eingefroren ? <span>{t("positionen.vorschlag.eingefroren", werte)}</span> : null}
        {/* Die Zubehörzeilen mit eigenem Preis gehen mit (05.10.2026, F3-Linie):
            je Zeile beide Beträge, darunter die Summe — ein Klick übernimmt alles. */}
        {vorschlag.anbauteile.map((a) => {
          const w = werteVon(a, a.was);
          return (
            <span key={a.id} data-anbauteil={a.id}>
              {t("positionen.vorschlag.anbauteil", w)}
              {a.preis_von_hand ? ` ${t("positionen.vorschlag.anbauteilVonHand", w)}` : ""}
              {a.eingefroren ? ` ${t("positionen.vorschlag.anbauteilEingefroren", w)}` : ""}
            </span>
          );
        })}
        {vorschlag.anbauteile.length ? (
          <span>
            {t("positionen.vorschlag.summe", { summe: euro(summe.neu), summeVorher: euro(summe.vorher) })}
          </span>
        ) : null}
        <span className="knopfreihe">
          <Knopf art="haupt" klein onClick={() => void uebernehmen()} disabled={laeuft}>
            {t("positionen.vorschlag.uebernehmen")}
          </Knopf>
          <Knopf klein onClick={fertig} disabled={laeuft}>{t("positionen.vorschlag.lassen")}</Knopf>
        </span>
      </div>
    </div>
  );
}
